/**
 * Phase 3.2 — OptiBoard lifecycle tab mapping.
 *
 * Doctrine: Bucket 3 §3.6.1 OptiBoard Lifecycle Tabs, §3.6.2 OptiBoard
 * Visibility Examples, §3.4.1–§3.4.11 production-state taxonomy.
 *
 * Why this module exists
 * ----------------------
 * Bucket 3 §3.6.1 says OptiBoard lifecycle tab placement is based on
 * production state, not physical location. Today the tab queries in
 * `useOptiboardTickets.ts` are SQL filters keyed on scattered fields
 * (`is_estimate`, `completed_at`, `picked_up_at`, `deleted_at`,
 * `timer_status`) — they happen to align correctly with the canonical
 * production states defined in Phase 1.1 (verified by the source-scan
 * test paired with this module), but the mapping is implicit. A future
 * refactor of the SQL could accidentally drift.
 *
 * This module gives callers (audit payloads, analytics, drift detectors,
 * any future client-side post-filter) a single canonical answer to:
 *
 *   "Which OptiBoard lifecycle tab does this ticket's production state
 *    belong to — or is it deliberately hidden from operational lifecycle
 *    surfaces?"
 *
 * Phase 3.2 scope: HELPER + TESTS ONLY.
 *   - Additive. Does NOT modify `useOptiboardTickets.ts` SQL filters, tab
 *     query semantics, pagination, or rendering behavior.
 *   - Does NOT alter Parking Lot / Birdseye physical-location views —
 *     those remain separate from lifecycle tabs by design.
 *   - Does NOT alter cascade, AutoBump, Due Out, OptiAssign, bay
 *     occupancy, or timer behavior.
 *   - No DB enum, no migration, no new column.
 *
 * The mapping below pins what is currently true and doctrinally correct.
 * The paired test file (`tests/unit/optiBoardLifecycleTab.test.ts`)
 * verifies both this helper's behavior AND that the SQL filters in
 * `useOptiboardTickets.ts` agree with this canonical mapping.
 */

import type { ProductionState } from './ticketProductionState';

// =============================================================================
// Tab taxonomy
// =============================================================================

/**
 * Canonical OptiBoard lifecycle tabs per Bucket 3 §3.6.1.
 *
 * Note: `'All'` is intentionally NOT in this union — that meta-tab shows
 * the union of every other tab and has no dedicated production-state
 * mapping.
 */
export type OptiBoardLifecycleTab =
  | 'Estimates'
  | 'Scheduled'
  | 'WIP'
  | 'Completed'
  | 'PickedUp'
  | 'Deleted';

/**
 * Frozen list of all canonical lifecycle tabs in display order.
 */
export const OPTIBOARD_LIFECYCLE_TABS = Object.freeze([
  'Estimates',
  'Scheduled',
  'WIP',
  'Completed',
  'PickedUp',
  'Deleted',
] as const) satisfies ReadonlyArray<OptiBoardLifecycleTab>;

// =============================================================================
// Canonical mapping
// =============================================================================

/**
 * Map a production state to its OptiBoard lifecycle tab, or `null` when
 * the state is intentionally hidden from operational lifecycle surfaces.
 *
 * Tab assignments (Bucket 3 §3.6.2):
 *
 *   - `estimate_prospect`         → 'Estimates'
 *   - `scheduled`                 → 'Scheduled'
 *   - `scheduled_but_parked`      → 'Scheduled'
 *       (Bucket 3 §3.4.3 — active scheduled work, just physically parked)
 *   - `active_in_bay`             → 'WIP'
 *   - `held_in_bay`               → 'WIP'
 *   - `held_in_parking`           → 'WIP'
 *       (Bucket 3 §3.4.6 — paused/held mid-execution, not yet completed)
 *   - `completed_awaiting_pickup` → 'Completed'
 *   - `picked_up`                 → 'PickedUp'
 *   - `deleted`                   → 'Deleted'
 *
 * Hidden from all lifecycle tabs (`null`):
 *
 *   - `parking_lot_inactive`  — Bucket 3 §3.4.11 doctrine: parked with
 *                               no active dispatch plan; surfaces only
 *                               via Parking Lot / Birdseye physical
 *                               location views, NOT via lifecycle tabs.
 *                               Reintroduction goes through the approved
 *                               workflow before the row regains a
 *                               lifecycle tab.
 *   - `archived`              — Bucket 3 §3.4.11: "Archived tickets do
 *                               not appear on the Dayboard / OptiBoard."
 *                               DB/history only.
 */
export function getOptiBoardLifecycleTabForProductionState(
  state: ProductionState,
): OptiBoardLifecycleTab | null {
  switch (state) {
    case 'estimate_prospect':
      return 'Estimates';
    case 'scheduled':
    case 'scheduled_but_parked':
      return 'Scheduled';
    case 'active_in_bay':
    case 'held_in_bay':
    case 'held_in_parking':
      return 'WIP';
    case 'completed_awaiting_pickup':
      return 'Completed';
    case 'picked_up':
      return 'PickedUp';
    case 'deleted':
      return 'Deleted';
    case 'parking_lot_inactive':
    case 'archived':
      return null;
  }
}

/**
 * Convenience predicate: does this production state belong to the given
 * lifecycle tab?
 *
 * Returns `false` for any state that maps to `null` (hidden from tabs).
 */
export function isProductionStateVisibleInOptiBoardTab(
  state: ProductionState,
  tab: OptiBoardLifecycleTab,
): boolean {
  return getOptiBoardLifecycleTabForProductionState(state) === tab;
}

/**
 * Reverse lookup: which production states are allowed in this tab?
 *
 * Returns a frozen array (in canonical order) of the production states
 * that map to the given tab. Useful for SQL drift detectors and for
 * building OR-clauses in future query refactors.
 */
export function getAllowedProductionStatesForOptiBoardTab(
  tab: OptiBoardLifecycleTab,
): ReadonlyArray<ProductionState> {
  switch (tab) {
    case 'Estimates':
      return Object.freeze(['estimate_prospect'] as const);
    case 'Scheduled':
      return Object.freeze(['scheduled', 'scheduled_but_parked'] as const);
    case 'WIP':
      return Object.freeze([
        'active_in_bay',
        'held_in_bay',
        'held_in_parking',
      ] as const);
    case 'Completed':
      return Object.freeze(['completed_awaiting_pickup'] as const);
    case 'PickedUp':
      return Object.freeze(['picked_up'] as const);
    case 'Deleted':
      return Object.freeze(['deleted'] as const);
  }
}

/**
 * Production states deliberately hidden from every lifecycle tab.
 *
 * These rows still exist in the DB and may be surfaced via other views
 * (Parking Lot / Birdseye for `parking_lot_inactive`; restore/audit for
 * `archived`), but they are NOT part of the active OptiBoard lifecycle
 * picture.
 */
export const HIDDEN_FROM_OPTIBOARD_TABS: ReadonlyArray<ProductionState> = Object.freeze(
  ['parking_lot_inactive', 'archived'] as const,
);
